import { useState, useCallback } from 'react'
import {
  calculateDurations,
  getTaskStatus
} from '../../../services/api/aiVideoGeneration'

const POLL_INTERVAL = 3000 // Poll every 3 seconds for duration calculation

/**
 * Custom hook to handle video duration calculation with polling
 */
export const useCalculateDurations = ({ videoId } = {}) => {
  const [taskId, setTaskId] = useState(null)
  const [isCalculating, setIsCalculating] = useState(false)
  const [progress, setProgress] = useState(0)
  const [status, setStatus] = useState(null)
  const [error, setError] = useState(null)

  const pollStatus = useCallback(async id => {
    try {
      const response = await getTaskStatus(id)
      const data = response?.data
      const currentStatus = data?.status || data?.state
      const currentProgress = data?.progress

      setStatus(currentStatus)
      if (currentProgress) {
        // Handle both "50%" string and 50 number
        const progressNum =
          typeof currentProgress === 'string'
            ? parseInt(currentProgress)
            : currentProgress
        setProgress(progressNum)
      }

      if (currentStatus === 'success') {
        setIsCalculating(false)
        return data?.output
      }

      if (currentStatus === 'failed' || currentStatus === 'error') {
        throw new Error(data?.message || 'Duration calculation failed')
      }

      // Continue polling
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
      return pollStatus(id)
    } catch (err) {
      console.error('Error polling duration status:', err)
      throw err
    }
  }, [])

  const calculate = useCallback(
    async (payload, formatter = null, ...formatterArgs) => {
      setIsCalculating(true)
      setProgress(0)
      setError(null)
      setStatus('starting')
      try {
        const response = await calculateDurations({
          shooting_list: payload.shooting_list,
          video_id: videoId
        })
        const id =
          response &&
          response.data &&
          (response.data.task_id ||
            response.data['task-id'] ||
            response.data.taskId)

        if (id) {
          setTaskId(id)
          setStatus('pending')
          const output = await pollStatus(id)

          // Apply formatter if provided
          if (formatter && typeof formatter === 'function') {
            return formatter(output, ...formatterArgs)
          }

          return output
        } else {
          throw new Error('No task_id returned from calculateDurations')
        }
      } catch (err) {
        console.error('Error in useCalculateDurations:', err)
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          'Failed to calculate durations'
        setError(errorMessage)
        setIsCalculating(false)
        throw err
      }
    },
    [pollStatus, videoId]
  )

  const resetCalculation = useCallback(() => {
    setTaskId(null)
    setIsCalculating(false)
    setProgress(0)
    setStatus(null)
    setError(null)
  }, [])

  return {
    taskId,
    isCalculating,
    progress,
    status,
    error,
    calculate,
    resetCalculation
  }
}

export default useCalculateDurations
